// ─── Weibull fit helpers ──────────────────────────────────────────────────────
// Two-parameter Weibull (β = shape, η = scale) fit by Maximum Likelihood
// Estimate. Surfaces the failure regime (infant mortality / random / wear-out)
// and supports B-life predictions on the failure-gap distribution dialog.

export interface WeibullFit {
    beta: number;       // shape parameter — < 1 infant mortality, ≈ 1 random, > 1 wear-out
    eta: number;        // scale parameter (characteristic life, in days)
    betaSE: number;     // asymptotic standard error
    etaSE: number;
    r2: number;         // coefficient of determination on the Weibull probability plot — see fitWeibullMLE
}

/** Two-parameter Weibull MLE via Newton-Raphson on the β profile-likelihood
 *  equation. Returns null when the sample is too small or the iteration fails
 *  to converge. */
export const fitWeibullMLE = (gaps: number[]): WeibullFit | null => {
    const positive = gaps.filter((g) => g > 0);
    const n = positive.length;
    if (n < 3) return null;
    const lnGaps = positive.map(Math.log);
    const meanLnGap = lnGaps.reduce((s, x) => s + x, 0) / n;
    let beta = 1.0;
    let converged = false;
    for (let iter = 0; iter < 200; iter++) {
        let sumTBeta = 0;
        let sumTBetaLnT = 0;
        let sumTBetaLnT2 = 0;
        for (let i = 0; i < n; i++) {
            const tBeta = Math.pow(positive[i], beta);
            sumTBeta += tBeta;
            sumTBetaLnT += tBeta * lnGaps[i];
            sumTBetaLnT2 += tBeta * lnGaps[i] * lnGaps[i];
        }
        if (sumTBeta === 0) return null;
        const A = sumTBetaLnT / sumTBeta;
        const B = sumTBetaLnT2 / sumTBeta;
        const f = 1 / beta - A + meanLnGap;
        const fprime = -1 / (beta * beta) - (B - A * A);
        if (fprime === 0) return null;
        const dBeta = f / fprime;
        beta -= dBeta;
        if (beta <= 0.01) beta = 0.01;
        if (Math.abs(dBeta) < 1e-7) { converged = true; break; }
    }
    if (!converged) return null;
    let sumTBetaFinal = 0;
    for (let i = 0; i < n; i++) sumTBetaFinal += Math.pow(positive[i], beta);
    const eta = Math.pow(sumTBetaFinal / n, 1 / beta);
    // Standard errors via Bain & Engelhardt asymptotic approximations.
    const betaSE = (beta * 0.78) / Math.sqrt(n);
    const etaSE = (eta * 1.04) / (beta * Math.sqrt(n));
    // Fit-quality: R² on the Weibull probability plot. Linearization of the Weibull
    // CDF says ln(−ln(1 − F)) is a linear function of ln(t) when the data is Weibull.
    // Empirical CDF uses the median-rank plotting position F̂_i = (i − 0.3) / (n + 0.4)
    // — the reliability-engineering standard. R² of the linear regression measures
    // how well the empirical points lie on a straight line in transformed space.
    // Unlike Anderson-Darling, R² is sample-size-stable: it does NOT punish large n.
    const sorted = [...positive].sort((a, b) => a - b);
    let sumX = 0, sumY = 0, sumXX = 0, sumYY = 0, sumXY = 0;
    let nPoints = 0;
    for (let i = 0; i < n; i++) {
        const t = sorted[i];
        if (t <= 0) continue;
        const F = (i + 1 - 0.3) / (n + 0.4);
        if (F <= 0 || F >= 1) continue;
        const x = Math.log(t);
        const y = Math.log(-Math.log(1 - F));
        sumX += x; sumY += y;
        sumXX += x * x; sumYY += y * y; sumXY += x * y;
        nPoints++;
    }
    let r2 = 1;
    if (nPoints >= 2) {
        const ssxx = sumXX - (sumX * sumX) / nPoints;
        const ssyy = sumYY - (sumY * sumY) / nPoints;
        const ssxy = sumXY - (sumX * sumY) / nPoints;
        r2 = ssxx > 0 && ssyy > 0 ? (ssxy * ssxy) / (ssxx * ssyy) : 0;
        if (!isFinite(r2)) r2 = 0;
        if (r2 < 0) r2 = 0;
        if (r2 > 1) r2 = 1;
    }
    return {beta, eta, betaSE, etaSE, r2};
};

export const weibullPDF = (t: number, beta: number, eta: number): number => {
    if (t <= 0 || eta <= 0) return 0;
    return (beta / eta) * Math.pow(t / eta, beta - 1) * Math.exp(-Math.pow(t / eta, beta));
};

export const weibullCDF = (t: number, beta: number, eta: number): number => {
    if (t <= 0) return 0;
    return 1 - Math.exp(-Math.pow(t / eta, beta));
};

/** Hazard rate: h(t) = f(t) / R(t). For Weibull this simplifies to
 *  (β/η) · (t/η)^(β−1). The conditional risk of failure at time t given survival
 *  to t. β < 1 → decreasing (infant mortality), β = 1 → constant (random),
 *  β > 1 → increasing (wear-out). Units are 1/time (failures per day here). */
export const weibullHazard = (t: number, beta: number, eta: number): number => {
    if (t <= 0 || eta <= 0) return 0;
    return (beta / eta) * Math.pow(t / eta, beta - 1);
};

/** Inverse Weibull CDF: the time at which a fraction p of items have failed. */
export const weibullBLife = (p: number, beta: number, eta: number): number => {
    return eta * Math.pow(-Math.log(1 - p), 1 / beta);
};

/** Classify the failure regime from β. Thresholds match the Bain & Engelhardt
 *  recommendation — narrowband around β = 1 reads as random rather than
 *  flapping between infant-mortality and wear-out on noise. */
export const weibullDiagnosis = (beta: number): {label: string; color: string; bgColor: string; arrow: string} => {
    if (beta < 0.85) return {label: 'Infant mortality', color: '#7b1fa2', bgColor: 'rgba(156, 39, 176, 0.12)', arrow: '↓'};
    if (beta > 1.15) return {label: 'Wear-out', color: '#d32f2f', bgColor: 'rgba(211, 47, 47, 0.12)', arrow: '↑'};
    return {label: 'Random / constant rate', color: '#1976d2', bgColor: 'rgba(25, 118, 210, 0.12)', arrow: '→'};
};

/** Fit-quality interpretation from R² on the Weibull probability plot.
 * Industry-standard thresholds used by reliability tooling (Weibull++ / Minitab / JMP).
 *  Sample-size-stable: the same R² means the same thing at n = 50 or n = 5000,
 *  which is what we need for NTSB-scale datasets where Anderson-Darling rejected
 *  every fit indiscriminately. */
export const weibullFitQuality = (r2: number): {label: string; symbol: string; color: string} => {
    if (r2 >= 0.95) return {label: 'excellent', symbol: '✓', color: '#2e7d32'};
    if (r2 >= 0.90) return {label: 'good', symbol: '✓', color: '#2e7d32'};
    if (r2 >= 0.85) return {label: 'marginal', symbol: '△', color: '#f57c00'};
    return {label: 'poor — Weibull may not be the right model', symbol: '✗', color: '#d32f2f'};
};
